import { useEffect, useSyncExternalStore } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Check, ExternalLink } from "lucide-react";

/**
 * The calendar journey's confirmations: one dark pill, top-centre under the nav on wider
 * screens and above the tab bar on phones. A module-level store, so a toast raised just
 * before navigating (Find a time → Momentum) is still there when the next page paints.
 */

interface PillToast {
  id: number;
  message: string;
  /** "warning" for something that didn't happen — same pill, warning mark instead of the check. */
  tone: "success" | "warning";
  action?: { label: string; href?: string; onClick?: () => void };
  durationMs: number;
}

let current: PillToast | null = null;
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function showPillToast(toast: {
  message: string;
  action?: PillToast["action"];
  durationMs?: number;
  tone?: PillToast["tone"];
}) {
  current = { id: nextId++, durationMs: 6000, tone: "success", ...toast };
  emit();
}

export function dismissPillToast() {
  current = null;
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function PillToaster() {
  const toast = useSyncExternalStore(subscribe, () => current);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => {
      if (current?.id === toast.id) dismissPillToast();
    }, toast.durationMs);
    return () => window.clearTimeout(t);
  }, [toast]);

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-24 z-[120] flex justify-center px-4 md:bottom-auto md:top-[84px]"
    >
      <AnimatePresence>
        {toast && (
          <motion.div
            key={toast.id}
            role="status"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, transition: { duration: 0.15 } }}
            transition={{ type: "spring", stiffness: 420, damping: 34 }}
            className="pointer-events-auto flex max-w-full items-center gap-3 rounded-[22px] bg-[#2f2925] py-2.5 pl-3 pr-5 md:max-w-[720px] text-[13px] text-white shadow-[0_12px_32px_-8px_rgba(47,41,37,0.45)] sm:text-sm"
          >
            {toast.tone === "warning" ? (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#e0b04a] text-[#2f2925]">
                <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.5} />
              </span>
            ) : (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary">
                <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
              </span>
            )}
            {/* Two lines at most, so a long confirmation still reads in full on a phone. */}
            <span className="min-w-0 line-clamp-2">{toast.message}</span>
            {toast.action &&
              (toast.action.href ? (
                <a
                  href={toast.action.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={dismissPillToast}
                  className="inline-flex shrink-0 items-center gap-1 font-medium text-[#c9b8f0] hover:text-white"
                >
                  {toast.action.label}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    toast.action?.onClick?.();
                    dismissPillToast();
                  }}
                  className="shrink-0 font-medium text-[#c9b8f0] hover:text-white"
                >
                  {toast.action.label}
                </button>
              ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
